import { CSSProperties } from 'react'
import { Box, SxProps } from '@mui/material'
import { keyframes } from '@emotion/react'

type IconProps = {
    color: string
    style?: CSSProperties
}

export const ProfileIcon = ({ color }: IconProps) => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <circle
                cx="11.1111"
                cy="7.74074"
                r="4.09074"
                stroke={color}
                strokeWidth="1.3"
            />
            <path
                d="M11.7037 14.8518H8.79613C7.41086 14.8518 6.12421 15.5685 5.39507 16.7464L4 19"
                stroke={color}
                strokeWidth="1.3"
                strokeLinecap="round"
            />
            <path
                d="M11.7038 14.8518H15"
                stroke={color}
                strokeWidth="1.3"
                strokeLinecap="round"
            />
            <rect
                x="18"
                y="14"
                width="1"
                height="7"
                rx="0.15"
                fill={color}
                stroke={color}
            />
            <rect
                x="22"
                y="17"
                width="1"
                height="7"
                rx="0.15"
                transform="rotate(90 22 17)"
                fill={color}
                stroke={color}
            />
        </svg>
    )
}

export const MessagesIcon = ({ color }: IconProps) => {
    return (
        <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4.8 9C3.2536 9 2 10.1939 2 11.6667V15.6667C2 17.1394 3.2536 18.3333 4.8 18.3333H6.2V21L9 18.3333H11.5C13.0464 18.3333 14.3 17.1394 14.3 15.6667V11.6667C14.3 10.1939 13.0464 9 11.5 9H4.8Z"
                stroke={color}
                strokeWidth="1.3"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <path
                d="M8 7V5.66667C8 4.19391 9.2536 3 10.8 3H19.2C20.7464 3 22 4.19391 22 5.66667V9.66667C22 11.1394 21.5464 12.3333 20 12.3333H19.5V15L17 12.3333H16.5"
                stroke={color}
                strokeWidth="1.3"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    )
}

export const LeftArrowIcon = ({ color, style = {} }: IconProps) => {
    return (
        <svg
            width="32"
            height="32"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
            style={{ ...style }}
        >
            <path
                fill={color}
                d="M7.46967 16.5303C7.17678 16.2374 7.17678 15.7626 7.46967 15.4697L12.2426 10.6967C12.5355 10.4038 13.0104 10.4038 13.3033 10.6967C13.5962 10.9896 13.5962 11.4645 13.3033 11.7574L9.06066 16L13.3033 20.2426C13.5962 20.5355 13.5962 21.0104 13.3033 21.3033C13.0104 21.5962 12.5355 21.5962 12.2426 21.3033L7.46967 16.5303ZM24 16.75L8 16.75L8 15.25L24 15.25L24 16.75Z"
            />
        </svg>
    )
}

type IndicatorProps = {
    sx?: SxProps
    variant?: 'primaryButton' | 'communicationBanner'
}

export const IndicatorIcon = ({ sx }: IndicatorProps) => {
    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                ...sx
            }}
        >
            <svg
                width="8"
                height="8"
                viewBox="0 0 8 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <circle cx="4" cy="4" r="4" fill={'#EF308D'} />
            </svg>
        </Box>
    )
}

export const ChevronRight = ({ color }: { color: string }) => {
    return (
        <svg width="8" height="12" viewBox="0 0 8 12" fill="none">
            <path
                d="M0.99998 0.704992C1.18681 0.517739 1.44046 0.412506 1.70498 0.412506C1.9695 0.412506 2.22315 0.517739 2.40998 0.704992L6.99998 5.29499C7.38998 5.68499 7.38998 6.31499 6.99998 6.70499L2.40998 11.295C2.01998 11.685 1.38998 11.685 0.99998 11.295C0.609981 10.905 0.609981 10.275 0.99998 9.88499L4.87998 5.99499L0.99998 2.11499C0.609981 1.72499 0.61998 1.08499 0.99998 0.704992Z"
                fill={color as string}
            />
        </svg>
    )
}

// cribbed from ThemeUI Spinner
// https://github.com/system-ui/theme-ui/blob/develop/packages/components/src/Spinner.js

// animation from
// https://loading.io/css/

export const animation1 = keyframes({
    from: {
        transform: 'scale(0)'
    },
    to: {
        transform: 'scale(1)'
    }
})

export const animation3 = keyframes({
    from: {
        transform: 'scale(1)'
    },
    to: {
        transform: 'scale(0)'
    }
})

export const animation2 = keyframes({
    from: {
        transform: 'translate(0, 0)'
    },
    to: {
        transform: 'translate(14px, 0)'
    }
})

export const Dot = ({ color, sx }: { color: string; sx: SxProps }) => (
    <Box
        sx={{
            position: 'absolute',
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            animationTimingFunction: 'cubic-bezier(0, 1, 1, 0)',
            backgroundColor: color,
            ...sx
        }}
    />
)

interface SpinnerProps {
    color?: string
    sx?: SxProps
}

const spinnerDefaultColor = '#831A4A'
const spinnerDefaultSx: SxProps = {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
}

export const Spinner = function ({
    color = spinnerDefaultColor,
    sx
}: SpinnerProps) {
    return (
        <Box
            role="img"
            sx={{
                ...spinnerDefaultSx,
                ...sx
            }}
        >
            <Box
                sx={{
                    width: '32px',
                    height: '32px',
                    position: 'relative'
                }}
            >
                <Dot
                    sx={{
                        left: '4px',
                        animation: `${animation1} 0.6s infinite`
                    }}
                    color={color}
                />
                <Dot
                    sx={{
                        left: '4px',
                        animation: `${animation2} 0.6s infinite`
                    }}
                    color={color}
                />
                <Dot
                    sx={{
                        left: '19px',
                        animation: `${animation2} 0.6s infinite`
                    }}
                    color={color}
                />
                <Dot
                    sx={{
                        left: '33px',
                        animation: `${animation3} 0.6s infinite`
                    }}
                    color={color}
                />
            </Box>
        </Box>
    )
}
